import { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	Package,
	Building2,
	Plus,
	Minus,
	CheckCircle2,
	AlertTriangle,
	Search,
	RotateCcw,
	ChevronDown,
	ChevronRight,
	DollarSign,
	FileText,
	X,
	Info,
	Link2,
	Ban,
} from "lucide-react";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/toaster";
import * as api from "@/services/api";
import type { Feature } from "@/types";
import { DependencyWarningDialog } from "@/components/features/DependencyWarningDialog";

interface PendingFeatureAction {
	featureSlug: string;
	action: "add" | "block" | "remove-custom" | "remove-blocked";
}

export function PackageManager() {
	const queryClient = useQueryClient();

	/*
	 * Selected organization, seeded from ?org= so a link from an
	 * organization's own page opens on that organization rather than an empty
	 * picker the reader then has to search in.
	 */
	const [searchParams] = useSearchParams();
	const [selectedOrgId, setSelectedOrgId] = useState<string>(
		() => searchParams.get("org") ?? "",
	);
	const [orgSearch, setOrgSearch] = useState("");

	// Form state (working copy)
	const [customPackageEnabled, setCustomPackageEnabled] = useState(false);
	const [customFeatures, setCustomFeatures] = useState<string[]>([]);
	const [blockedFeatures, setBlockedFeatures] = useState<string[]>([]);
	const [notes, setNotes] = useState("");

	// Custom pricing state
	const [pricingEnabled, setPricingEnabled] = useState(false);
	const [price, setPrice] = useState<number>(0);
	const [currency, setCurrency] = useState("USD");
	const [billingPeriod, setBillingPeriod] = useState<"monthly" | "yearly">(
		"monthly",
	);
	const [customTerms, setCustomTerms] = useState("");

	// UI state
	const [expandedModules, setExpandedModules] = useState<Set<string>>(
		new Set(),
	);
	const [featureSearch, setFeatureSearch] = useState("");
	const [showResetConfirm, setShowResetConfirm] = useState(false);

	// Track if form has unsaved changes
	const [isDirty, setIsDirty] = useState(false);

	// Dialog state for dependency warnings
	const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogMode, setDialogMode] = useState<"enable" | "disable" | "block">(
		"enable",
	);
	const [pendingAction, setPendingAction] =
		useState<PendingFeatureAction | null>(null);
	const [autoGrantedDeps, setAutoGrantedDeps] = useState<
		api.AutoGrantedPermission[]
	>([]);
	const [affectedDependents, setAffectedDependents] = useState<
		Array<{ feature: string; featureName: string; reason?: string }>
	>([]);

	// Queries
	const { data: orgsResponse, isLoading: orgsLoading } = useQuery({
		queryKey: ["admin-organizations"],
		queryFn: () => api.getAdminOrganizations({ limit: 100 }),
	});

	const { data: features } = useQuery({
		queryKey: ["features"],
		queryFn: api.getFeatures,
	});

	const { data: plans } = useQuery({
		queryKey: ["plans"],
		queryFn: api.getPlans,
	});

	const { data: currencies = [] } = useQuery({
		queryKey: ["admin-currencies"],
		queryFn: () => api.getCurrencies(),
	});

	const { data: customPackage, isLoading: packageLoading } = useQuery({
		queryKey: ["custom-package", selectedOrgId],
		queryFn: () => api.getCustomPackage(selectedOrgId),
		enabled: !!selectedOrgId,
	});

	useQuery({
		queryKey: ["effective-features", selectedOrgId],
		queryFn: () => api.getEffectiveFeatures(selectedOrgId),
		enabled: !!selectedOrgId,
	});

	// Fetch dependency information
	const { data: dependencyInfo } = useQuery({
		queryKey: ["features-dependency-info"],
		queryFn: api.getFeaturesWithDependencyInfo,
	});

	// Filter organizations by search
	const filteredOrgs = useMemo(() => {
		if (!orgsResponse?.organizations) return [];
		if (!orgSearch) return orgsResponse.organizations;
		const search = orgSearch.toLowerCase();
		return orgsResponse.organizations.filter(
			(org) =>
				org.name.toLowerCase().includes(search) ||
				org.slug?.toLowerCase().includes(search) ||
				org.plan.toLowerCase().includes(search),
		);
	}, [orgsResponse, orgSearch]);

	// Get selected organization
	const selectedOrg = useMemo(() => {
		return orgsResponse?.organizations?.find((o) => o._id === selectedOrgId);
	}, [orgsResponse, selectedOrgId]);

	// Get selected plan
	const selectedPlan = useMemo(() => {
		return plans?.find((p) => p.slug === selectedOrg?.plan);
	}, [plans, selectedOrg]);

	// Base plan features (normalize: backend may return feature objects or strings)
	const basePlanFeatures = useMemo(() => {
		return (selectedPlan?.features || []).map((f: any) =>
			typeof f === "string" ? f : f.slug || f.code,
		);
	}, [selectedPlan]);

	// Calculate effective features (base - blocked + custom)
	const effectiveFeatures = useMemo(() => {
		return [
			...basePlanFeatures.filter((f) => !blockedFeatures.includes(f)),
			...customFeatures.filter((f) => !basePlanFeatures.includes(f)),
		];
	}, [basePlanFeatures, customFeatures, blockedFeatures]);

	// Group features by module
	const groupedFeatures = useMemo(() => {
		if (!features) return {};
		return features.reduce(
			(acc, feature) => {
				const module = feature.module || "other";
				if (!acc[module]) acc[module] = [];
				acc[module].push(feature);
				return acc;
			},
			{} as Record<string, Feature[]>,
		);
	}, [features]);

	// Features available to add (not in base plan and not already custom)
	const availableToAdd = useMemo(() => {
		if (!features) return [];
		return features.filter(
			(f) =>
				!basePlanFeatures.includes(f.slug) &&
				!customFeatures.includes(f.slug) &&
				(featureSearch
					? f.slug.toLowerCase().includes(featureSearch.toLowerCase()) ||
						(f.ui?.label || "")
							.toLowerCase()
							.includes(featureSearch.toLowerCase())
					: true),
		);
	}, [features, basePlanFeatures, customFeatures, featureSearch]);

	// Features available to block (in base plan and not already blocked)
	const availableToBlock = useMemo(() => {
		if (!features) return [];
		return features.filter(
			(f) =>
				basePlanFeatures.includes(f.slug) &&
				!blockedFeatures.includes(f.slug) &&
				(featureSearch
					? f.slug.toLowerCase().includes(featureSearch.toLowerCase()) ||
						(f.ui?.label || "")
							.toLowerCase()
							.includes(featureSearch.toLowerCase())
					: true),
		);
	}, [features, basePlanFeatures, blockedFeatures, featureSearch]);

	// Calculate preview effective features
	const previewEffective = useMemo(() => {
		return [
			...basePlanFeatures.filter((f) => !blockedFeatures.includes(f)),
			...customFeatures.filter((f) => !basePlanFeatures.includes(f)),
		];
	}, [basePlanFeatures, customFeatures, blockedFeatures]);

	// Helper to get feature name from slug
	const getFeatureName = (slug: string) => {
		const feature = features?.find((f) => f.slug === slug);
		return feature?.ui?.label || feature?.name || slug;
	};

	// Check dependencies for a feature
	const getFeatureDependencyInfo = (featureSlug: string) => {
		const code = featureSlug?.toLowerCase();
		const deps = dependencyInfo?.dependencyMap[code] || [];
		const dependentCodes = dependencyInfo?.reverseDependencyMap[code] || [];

		// Calculate missing dependencies (not in effective features)
		const missingDeps = deps.filter(
			(dep) =>
				!effectiveFeatures.some(
					(f) => f.toLowerCase() === dep.feature.toLowerCase(),
				),
		);

		// Calculate enabled dependents (in effective features)
		const enabledDependents = dependentCodes.filter((depCode) =>
			effectiveFeatures.some((f) => f.toLowerCase() === depCode.toLowerCase()),
		);

		return {
			hasDependencies: deps.length > 0,
			dependencies: deps,
			dependencyCount: deps.length,
			missingDependencies: missingDeps,
			hasDependents: dependentCodes.length > 0,
			dependents: dependentCodes,
			dependentCount: dependentCodes.length,
			enabledDependents,
		};
	};

	// Mutations
	const updateMutation = useMutation({
		mutationFn: (data: {
			enabled: boolean;
			customFeatures: string[];
			blockedFeatures: string[];
			customPricing?: {
				price: number;
				currency: string;
				billingPeriod: "monthly" | "yearly";
				customTerms?: string;
			};
			notes?: string;
		}) => api.updateCustomPackage(selectedOrgId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["custom-package", selectedOrgId],
			});
			queryClient.invalidateQueries({
				queryKey: ["effective-features", selectedOrgId],
			});
			queryClient.invalidateQueries({ queryKey: ["admin-organizations"] });
			toast({ title: "Custom package saved successfully", variant: "success" });
			setIsDirty(false);
		},
		onError: (error) => {
			toast({
				title: "Failed to save custom package",
				description: String(error),
				variant: "destructive",
			});
		},
	});

	const resetMutation = useMutation({
		mutationFn: () => api.resetToBasePlan(selectedOrgId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["custom-package", selectedOrgId],
			});
			queryClient.invalidateQueries({
				queryKey: ["effective-features", selectedOrgId],
			});
			queryClient.invalidateQueries({ queryKey: ["admin-organizations"] });
			toast({ title: "Reset to base plan successfully", variant: "success" });
			// Reset form state
			setCustomPackageEnabled(false);
			setCustomFeatures([]);
			setBlockedFeatures([]);
			setNotes("");
			setPricingEnabled(false);
			setPrice(0);
			setCurrency("USD");
			setBillingPeriod("monthly");
			setCustomTerms("");
			setIsDirty(false);
			setShowResetConfirm(false);
		},
		onError: (error) => {
			toast({
				title: "Failed to reset to base plan",
				description: String(error),
				variant: "destructive",
			});
		},
	});

	// Handle organization selection
	const handleOrgSelect = (orgId: string) => {
		if (isDirty) {
			if (!confirm("You have unsaved changes. Discard them?")) {
				return;
			}
		}
		setSelectedOrgId(orgId);
		setIsDirty(false);
	};

	// Load custom package data into form when it changes
	useMemo(() => {
		if (customPackage?.customPackage) {
			const pkg = customPackage.customPackage;
			setCustomPackageEnabled(pkg.enabled);
			setCustomFeatures(pkg.customFeatures || []);
			setBlockedFeatures(pkg.blockedFeatures || []);
			setNotes(pkg.notes || "");
			if (pkg.customPricing) {
				setPricingEnabled(true);
				setPrice(pkg.customPricing.price);
				setCurrency(pkg.customPricing.currency);
				setBillingPeriod(pkg.customPricing.billingPeriod);
				setCustomTerms(pkg.customPricing.customTerms || "");
			} else {
				setPricingEnabled(false);
				setPrice(0);
				setCurrency("USD");
				setBillingPeriod("monthly");
				setCustomTerms("");
			}
		} else if (customPackage) {
			// No custom package, reset form
			setCustomPackageEnabled(false);
			setCustomFeatures([]);
			setBlockedFeatures([]);
			setNotes("");
			setPricingEnabled(false);
			setPrice(0);
			setCurrency("USD");
			setBillingPeriod("monthly");
			setCustomTerms("");
		}
	}, [customPackage]);

	// Toggle module expansion
	const toggleModule = (module: string) => {
		setExpandedModules((prev) => {
			const next = new Set(prev);
			if (next.has(module)) {
				next.delete(module);
			} else {
				next.add(module);
			}
			return next;
		});
	};

	// Toggle custom feature with dependency check
	const toggleCustomFeature = (featureSlug: string) => {
		const depInfo = getFeatureDependencyInfo(featureSlug);

		// Check for missing dependencies
		if (depInfo.missingDependencies.length > 0) {
			setAutoGrantedDeps(
				depInfo.missingDependencies.map((dep) => ({
					feature: dep.feature,
					action: dep.action,
					requiredBy: featureSlug,
					reason: dep.reason,
				})),
			);
			setDialogMode("enable");
			setPendingAction({ featureSlug, action: "add" });
			setDialogOpen(true);
			return;
		}

		// No issues, proceed
		executeAddCustomFeature(featureSlug);
	};

	const executeAddCustomFeature = (featureSlug: string) => {
		setCustomFeatures((prev) => {
			if (prev.includes(featureSlug)) {
				return prev.filter((f) => f !== featureSlug);
			}
			// Remove from blocked if present
			setBlockedFeatures((blocked) =>
				blocked.filter((f) => f !== featureSlug),
			);
			return [...prev, featureSlug];
		});
		setIsDirty(true);
	};

	// Toggle blocked feature with dependency check
	const toggleBlockedFeature = (featureSlug: string) => {
		const depInfo = getFeatureDependencyInfo(featureSlug);

		// Check if any enabled features depend on this
		if (depInfo.enabledDependents.length > 0) {
			setAffectedDependents(
				depInfo.enabledDependents.map((depCode) => {
					const depFeatureDeps =
						dependencyInfo?.dependencyMap[depCode.toLowerCase()] || [];
					const reasonDep = depFeatureDeps.find(
						(d) => d.feature.toLowerCase() === featureSlug.toLowerCase(),
					);
					return {
						feature: depCode,
						featureName: getFeatureName(depCode),
						reason: reasonDep?.reason,
					};
				}),
			);
			setDialogMode("block");
			setPendingAction({ featureSlug, action: "block" });
			setDialogOpen(true);
			return;
		}

		// No issues, proceed
		executeBlockFeature(featureSlug);
	};

	const executeBlockFeature = (featureSlug: string) => {
		setBlockedFeatures((prev) => {
			if (prev.includes(featureSlug)) {
				return prev.filter((f) => f !== featureSlug);
			}
			// Remove from custom if present
			setCustomFeatures((custom) => custom.filter((f) => f !== featureSlug));
			return [...prev, featureSlug];
		});
		setIsDirty(true);
	};

	// Remove from custom features
	const removeCustomFeature = (featureSlug: string) => {
		const depInfo = getFeatureDependencyInfo(featureSlug);

		// Check if any enabled features depend on this
		if (depInfo.enabledDependents.length > 0) {
			setAffectedDependents(
				depInfo.enabledDependents.map((depCode) => {
					const depFeatureDeps =
						dependencyInfo?.dependencyMap[depCode.toLowerCase()] || [];
					const reasonDep = depFeatureDeps.find(
						(d) => d.feature.toLowerCase() === featureSlug.toLowerCase(),
					);
					return {
						feature: depCode,
						featureName: getFeatureName(depCode),
						reason: reasonDep?.reason,
					};
				}),
			);
			setDialogMode("disable");
			setPendingAction({ featureSlug, action: "remove-custom" });
			setDialogOpen(true);
			return;
		}

		setCustomFeatures((prev) => prev.filter((f) => f !== featureSlug));
		setIsDirty(true);
	};

	// Remove from blocked features
	const removeBlockedFeature = (featureSlug: string) => {
		setBlockedFeatures((prev) => prev.filter((f) => f !== featureSlug));
		setIsDirty(true);
	};

	// Handle dialog confirmation
	const handleDialogConfirm = () => {
		if (!pendingAction) return;

		if (pendingAction.action === "add" && autoGrantedDeps.length > 0) {
			// Add the feature and all its missing dependencies
			const featuresToAdd = [
				pendingAction.featureSlug,
				...autoGrantedDeps
					.map((dep) => dep.feature)
					.filter(
						(f) =>
							!customFeatures.includes(f) &&
							!basePlanFeatures.includes(f),
					),
			];

			setCustomFeatures((prev) => [...prev, ...featuresToAdd]);
			setIsDirty(true);
		} else if (pendingAction.action === "block") {
			// Cannot block - dialog will just close
		} else if (pendingAction.action === "remove-custom") {
			// Remove with warning acknowledged
			setCustomFeatures((prev) =>
				prev.filter((f) => f !== pendingAction.featureSlug),
			);
			setIsDirty(true);
		}

		// Reset dialog state
		setDialogOpen(false);
		setPendingAction(null);
		setAutoGrantedDeps([]);
		setAffectedDependents([]);
	};

	const handleDialogCancel = () => {
		setDialogOpen(false);
		setPendingAction(null);
		setAutoGrantedDeps([]);
		setAffectedDependents([]);
	};

	// Save changes
	const handleSave = () => {
		const data: Parameters<typeof updateMutation.mutate>[0] = {
			enabled: customPackageEnabled,
			customFeatures,
			blockedFeatures,
			notes: notes || undefined,
		};

		if (pricingEnabled && price > 0) {
			data.customPricing = {
				price,
				currency,
				billingPeriod,
				customTerms: customTerms || undefined,
			};
		}

		updateMutation.mutate(data);
	};

	// Loading state
	if (orgsLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Header */}
			<div>
				<h1 className="text-3xl font-bold text-gray-900">
					Custom Package Manager
				</h1>
				<p className="text-gray-600 mt-2">
					Customize feature packages for individual organizations beyond their
					base plan
				</p>
			</div>

			{/* Organization Selector */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Building2 className="w-5 h-5" />
						Select Organization
					</CardTitle>
					<CardDescription>
						Search and select an organization to configure its custom package
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						{/* Search input */}
						<div className="relative">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
							<Input
								placeholder="Search organizations..."
								value={orgSearch}
								onChange={(e) => setOrgSearch(e.target.value)}
								className="pl-10"
							/>
						</div>

						{/* Organization select */}
						<Select value={selectedOrgId} onValueChange={handleOrgSelect}>
							<SelectTrigger className="w-full">
								<SelectValue placeholder="Select organization..." />
							</SelectTrigger>
							<SelectContent>
								{filteredOrgs.map((org) => (
									<SelectItem key={org._id} value={org._id}>
										<div className="flex items-center gap-3">
											<Building2 className="w-4 h-4 text-gray-400" />
											<span className="font-medium">{org.name}</span>
											<Badge variant="outline" className="text-xs">
												{org.plan}
											</Badge>
											{org.hasCustomPackage && (
												<Badge variant="secondary" className="text-xs">
													Custom
												</Badge>
											)}
										</div>
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</CardContent>
			</Card>

			{/* Loading state for package */}
			{packageLoading && selectedOrgId && (
				<div className="flex items-center justify-center h-32">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
				</div>
			)}

			{/* Organization details and package management */}
			{selectedOrg && !packageLoading && (
				<>
					{/* Dependency Info Banner */}
					{dependencyInfo && (
						<Card className="border-blue-200 bg-blue-50">
							<CardContent className="py-3">
								<div className="flex items-center gap-2 text-sm text-blue-700">
									<Info className="w-4 h-4" />
									<span>
										<strong>Dependency Validation Active:</strong> When adding
										features, missing dependencies will be suggested. You cannot
										block features that other enabled features depend on.
									</span>
								</div>
							</CardContent>
						</Card>
					)}

					{/* Current State Card */}
					<Card>
						<CardHeader>
							<div className="flex items-center justify-between">
								<div>
									<CardTitle className="flex items-center gap-2">
										<Package className="w-5 h-5" />
										{selectedOrg.name}
									</CardTitle>
									<CardDescription>
										Base Plan:{" "}
										<span className="font-medium">
											{selectedPlan?.name || selectedOrg.plan}
										</span>
										{selectedPlan?.monthlyPrice != null && selectedPlan.monthlyPrice > 0 && (
											<span className="ml-2">
												(${selectedPlan.monthlyPrice}/month)
											</span>
										)}
									</CardDescription>
								</div>
								<div className="flex items-center gap-4">
									<Badge
										variant={
											selectedOrg.subscriptionStatus === "active"
												? "success"
												: selectedOrg.subscriptionStatus === "trialing"
													? "default"
													: "secondary"
										}
									>
										{selectedOrg.subscriptionStatus || "unknown"}
									</Badge>
									{isDirty && (
										<Badge
											variant="outline"
											className="text-yellow-600 border-yellow-600"
										>
											Unsaved changes
										</Badge>
									)}
								</div>
							</div>
						</CardHeader>
						<CardContent>
							<div className="grid grid-cols-4 gap-4">
								<div className="text-center p-4 bg-gray-50 rounded-lg">
									<p className="text-sm text-muted-foreground">Base Features</p>
									<p className="text-3xl font-bold">
										{basePlanFeatures.length}
									</p>
								</div>
								<div className="text-center p-4 bg-green-50 rounded-lg">
									<p className="text-sm text-green-700">Custom Added</p>
									<p className="text-3xl font-bold text-green-600">
										+{customFeatures.length}
									</p>
								</div>
								<div className="text-center p-4 bg-red-50 rounded-lg">
									<p className="text-sm text-red-700">Blocked</p>
									<p className="text-3xl font-bold text-red-600">
										-{blockedFeatures.length}
									</p>
								</div>
								<div className="text-center p-4 bg-blue-50 rounded-lg">
									<p className="text-sm text-blue-700">Effective Total</p>
									<p className="text-3xl font-bold text-blue-600">
										{previewEffective.length}
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					{/* Custom Package Toggle */}
					<Card>
						<CardHeader>
							<div className="flex items-center justify-between">
								<div>
									<CardTitle>Enable Custom Package</CardTitle>
									<CardDescription>
										Toggle to enable customizations beyond the base plan
									</CardDescription>
								</div>
								<Switch
									checked={customPackageEnabled}
									onCheckedChange={(checked) => {
										setCustomPackageEnabled(checked);
										setIsDirty(true);
									}}
								/>
							</div>
						</CardHeader>
					</Card>

					{customPackageEnabled && (
						<>
							{/* Feature Management */}
							<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
								{/* Add Custom Features */}
								<Card>
									<CardHeader>
										<CardTitle className="flex items-center gap-2 text-green-700">
											<Plus className="w-5 h-5" />
											Add Custom Features
										</CardTitle>
										<CardDescription>
											Features not in the base plan that can be added
										</CardDescription>
									</CardHeader>
									<CardContent>
										{/* Currently Added */}
										{customFeatures.length > 0 && (
											<div className="mb-4">
												<p className="text-sm font-medium mb-2">
													Currently Added:
												</p>
												<div className="flex flex-wrap gap-2">
													{customFeatures.map((slug) => {
														const depInfo = getFeatureDependencyInfo(slug);
														return (
															<Badge
																key={slug}
																variant="outline"
																className="cursor-pointer hover:bg-red-50 border-green-500 text-green-700 pr-1"
															>
																{getFeatureName(slug)}
																{depInfo.enabledDependents.length > 0 && (
																	<span
																		className="ml-1 text-yellow-600"
																		title={`${depInfo.enabledDependents.length} feature(s) depend on this`}
																	>
																		<AlertTriangle className="w-3 h-3 inline" />
																	</span>
																)}
																<button
																	type="button"
																	onClick={() => removeCustomFeature(slug)}
																	className="ml-1 hover:text-red-600"
																>
																	<X className="w-3 h-3" />
																</button>
															</Badge>
														);
													})}
												</div>
											</div>
										)}

										{/* Available to Add */}
										<div className="relative mb-3">
											<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
											<Input
												placeholder="Search features..."
												value={featureSearch}
												onChange={(e) => setFeatureSearch(e.target.value)}
												className="pl-10"
											/>
										</div>

										<div className="max-h-64 overflow-y-auto space-y-1 border rounded-lg p-2">
											{Object.entries(groupedFeatures).map(
												([module, moduleFeatures]) => {
													const availableInModule = moduleFeatures.filter(
														(f) =>
															!basePlanFeatures.includes(f.slug) &&
															!customFeatures.includes(f.slug) &&
															(featureSearch
																? f.slug
																		.toLowerCase()
																		.includes(featureSearch.toLowerCase()) ||
																	(f.ui?.label || "")
																		.toLowerCase()
																		.includes(featureSearch.toLowerCase())
																: true),
													);
													if (availableInModule.length === 0) return null;

													return (
														<div key={module}>
															<button
																type="button"
																onClick={() => toggleModule(`add-${module}`)}
																className="flex items-center gap-2 w-full px-2 py-1 text-sm font-semibold text-gray-600 hover:bg-gray-50 rounded"
															>
																{expandedModules.has(`add-${module}`) ? (
																	<ChevronDown className="w-4 h-4" />
																) : (
																	<ChevronRight className="w-4 h-4" />
																)}
																{module}
																<Badge
																	variant="secondary"
																	className="text-xs ml-auto"
																>
																	{availableInModule.length}
																</Badge>
															</button>
															{expandedModules.has(`add-${module}`) && (
																<div className="ml-6 space-y-1">
																	{availableInModule.map((feature) => {
																		const depInfo = getFeatureDependencyInfo(
																			feature.slug,
																		);
																		const hasMissingDeps =
																			depInfo.missingDependencies.length > 0;

																		return (
																			<div
																				key={feature.slug}
																				className="flex items-center gap-2 p-2 hover:bg-green-50 rounded cursor-pointer"
																				onClick={() =>
																					toggleCustomFeature(feature.slug)
																				}
																			>
																				<Checkbox
																					checked={customFeatures.includes(
																						feature.slug,
																					)}
																					className="data-[state=checked]:bg-green-600"
																				/>
																				<span className="text-sm flex-1">
																					{feature.ui?.label || feature.slug}
																				</span>
																				{depInfo.hasDependencies && (
																					<Badge
																						variant="outline"
																						className={`text-[9px] px-1 py-0 ${hasMissingDeps ? "border-yellow-400 text-yellow-600" : "border-green-400 text-green-600"}`}
																						title={
																							hasMissingDeps
																								? `Missing ${depInfo.missingDependencies.length} dependency(ies)`
																								: "All dependencies satisfied"
																						}
																					>
																						<Link2 className="w-2.5 h-2.5 mr-0.5" />
																						{depInfo.dependencyCount}
																					</Badge>
																				)}
																				<Plus className="w-4 h-4 text-green-600" />
																			</div>
																		);
																	})}
																</div>
															)}
														</div>
													);
												},
											)}
											{availableToAdd.length === 0 && (
												<p className="text-center py-4 text-gray-500 text-sm">
													No features available to add
												</p>
											)}
										</div>
									</CardContent>
								</Card>

								{/* Block Features */}
								<Card>
									<CardHeader>
										<CardTitle className="flex items-center gap-2 text-red-700">
											<Minus className="w-5 h-5" />
											Block Features
										</CardTitle>
										<CardDescription>
											Features in the base plan that can be blocked
										</CardDescription>
									</CardHeader>
									<CardContent>
										{/* Currently Blocked */}
										{blockedFeatures.length > 0 && (
											<div className="mb-4">
												<p className="text-sm font-medium mb-2">
													Currently Blocked:
												</p>
												<div className="flex flex-wrap gap-2">
													{blockedFeatures.map((slug) => (
														<Badge
															key={slug}
															variant="outline"
															className="cursor-pointer hover:bg-green-50 border-red-500 text-red-700 pr-1"
														>
															{getFeatureName(slug)}
															<button
																type="button"
																onClick={() => removeBlockedFeature(slug)}
																className="ml-1 hover:text-green-600"
															>
																<X className="w-3 h-3" />
															</button>
														</Badge>
													))}
												</div>
											</div>
										)}

										{/* Available to Block */}
										<div className="max-h-64 overflow-y-auto space-y-1 border rounded-lg p-2 mt-12">
											{Object.entries(groupedFeatures).map(
												([module, moduleFeatures]) => {
													const availableInModule = moduleFeatures.filter(
														(f) =>
															basePlanFeatures.includes(f.slug) &&
															!blockedFeatures.includes(f.slug) &&
															(featureSearch
																? f.slug
																		.toLowerCase()
																		.includes(featureSearch.toLowerCase()) ||
																	(f.ui?.label || "")
																		.toLowerCase()
																		.includes(featureSearch.toLowerCase())
																: true),
													);
													if (availableInModule.length === 0) return null;

													return (
														<div key={module}>
															<button
																type="button"
																onClick={() => toggleModule(`block-${module}`)}
																className="flex items-center gap-2 w-full px-2 py-1 text-sm font-semibold text-gray-600 hover:bg-gray-50 rounded"
															>
																{expandedModules.has(`block-${module}`) ? (
																	<ChevronDown className="w-4 h-4" />
																) : (
																	<ChevronRight className="w-4 h-4" />
																)}
																{module}
																<Badge
																	variant="secondary"
																	className="text-xs ml-auto"
																>
																	{availableInModule.length}
																</Badge>
															</button>
															{expandedModules.has(`block-${module}`) && (
																<div className="ml-6 space-y-1">
																	{availableInModule.map((feature) => {
																		const depInfo = getFeatureDependencyInfo(
																			feature.slug,
																		);
																		const cannotBlock =
																			depInfo.enabledDependents.length > 0;

																		return (
																			<div
																				key={feature.slug}
																				className={`flex items-center gap-2 p-2 rounded cursor-pointer ${cannotBlock ? "opacity-50 hover:bg-gray-50" : "hover:bg-red-50"}`}
																				onClick={() =>
																					toggleBlockedFeature(feature.slug)
																				}
																				title={
																					cannotBlock
																						? `Cannot block: ${depInfo.enabledDependents.length} feature(s) depend on this`
																						: undefined
																				}
																			>
																				<Checkbox
																					checked={blockedFeatures.includes(
																						feature.slug,
																					)}
																					className="data-[state=checked]:bg-red-600"
																				/>
																				<span className="text-sm flex-1">
																					{feature.ui?.label || feature.slug}
																				</span>
																				{cannotBlock && (
																					<Badge
																						variant="outline"
																						className="text-[9px] px-1 py-0 border-orange-400 text-orange-600"
																						title={`${depInfo.enabledDependents.length} feature(s) depend on this`}
																					>
																						<Ban className="w-2.5 h-2.5 mr-0.5" />
																						{depInfo.enabledDependents.length}
																					</Badge>
																				)}
																				<Minus className="w-4 h-4 text-red-600" />
																			</div>
																		);
																	})}
																</div>
															)}
														</div>
													);
												},
											)}
											{availableToBlock.length === 0 && (
												<p className="text-center py-4 text-gray-500 text-sm">
													No features available to block
												</p>
											)}
										</div>
									</CardContent>
								</Card>
							</div>

							{/* Effective Features Preview */}
							<Card>
								<CardHeader>
									<CardTitle>Effective Features Preview</CardTitle>
									<CardDescription>
										Visual breakdown of what features this organization will
										have access to
									</CardDescription>
								</CardHeader>
								<CardContent>
									<div className="grid grid-cols-3 gap-4 mb-4">
										{/* Base features */}
										<div>
											<p className="text-sm font-medium text-gray-600 mb-2">
												Base Plan ({basePlanFeatures.length})
											</p>
											<div className="max-h-48 overflow-y-auto space-y-1">
												{basePlanFeatures.map((slug) => (
													<Badge
														key={slug}
														variant="outline"
														className={`mr-1 mb-1 text-xs ${
															blockedFeatures.includes(slug)
																? "line-through text-red-400 border-red-300"
																: ""
														}`}
													>
														{getFeatureName(slug)}
													</Badge>
												))}
											</div>
										</div>

										{/* Custom additions */}
										<div>
											<p className="text-sm font-medium text-green-600 mb-2">
												+ Custom ({customFeatures.length})
											</p>
											<div className="max-h-48 overflow-y-auto space-y-1">
												{customFeatures.map((slug) => (
													<Badge
														key={slug}
														variant="outline"
														className="mr-1 mb-1 text-xs border-green-500 text-green-700"
													>
														<Plus className="w-3 h-3 mr-1" />
														{getFeatureName(slug)}
													</Badge>
												))}
												{customFeatures.length === 0 && (
													<p className="text-xs text-gray-400">
														No custom features added
													</p>
												)}
											</div>
										</div>

										{/* Blocked */}
										<div>
											<p className="text-sm font-medium text-red-600 mb-2">
												- Blocked ({blockedFeatures.length})
											</p>
											<div className="max-h-48 overflow-y-auto space-y-1">
												{blockedFeatures.map((slug) => (
													<Badge
														key={slug}
														variant="outline"
														className="mr-1 mb-1 text-xs border-red-500 text-red-700"
													>
														<Minus className="w-3 h-3 mr-1" />
														{getFeatureName(slug)}
													</Badge>
												))}
												{blockedFeatures.length === 0 && (
													<p className="text-xs text-gray-400">
														No features blocked
													</p>
												)}
											</div>
										</div>
									</div>

									{/* Final effective count */}
									<div className="p-4 bg-blue-50 rounded-lg">
										<p className="text-sm font-medium text-blue-700 mb-2">
											Final Effective Features: {previewEffective.length}
										</p>
										<div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
											{previewEffective.slice(0, 30).map((slug) => (
												<Badge
													key={slug}
													variant="secondary"
													className="text-xs"
												>
													{getFeatureName(slug)}
												</Badge>
											))}
											{previewEffective.length > 30 && (
												<Badge variant="outline" className="text-xs">
													+{previewEffective.length - 30} more
												</Badge>
											)}
										</div>
									</div>
								</CardContent>
							</Card>

							{/* Custom Pricing (Collapsible) */}
							<Card>
								<CardHeader>
									<div className="flex items-center justify-between">
										<div>
											<CardTitle className="flex items-center gap-2">
												<DollarSign className="w-5 h-5" />
												Custom Pricing
											</CardTitle>
											<CardDescription>
												Override the base plan pricing for this organization
											</CardDescription>
										</div>
										<Switch
											checked={pricingEnabled}
											onCheckedChange={(checked) => {
												setPricingEnabled(checked);
												setIsDirty(true);
											}}
										/>
									</div>
								</CardHeader>
								{pricingEnabled && (
									<CardContent>
										<div className="grid grid-cols-3 gap-4">
											<div>
												<label className="text-sm font-medium">Price</label>
												<Input
													type="number"
													value={price}
													onChange={(e) => {
														setPrice(Number(e.target.value));
														setIsDirty(true);
													}}
													min={0}
													step={0.01}
													className="mt-1"
												/>
											</div>
											<div>
												<label className="text-sm font-medium">Currency</label>
												<Select
													value={currency}
													onValueChange={(value) => {
														setCurrency(value);
														setIsDirty(true);
													}}
												>
													<SelectTrigger className="mt-1">
														<SelectValue />
													</SelectTrigger>
													<SelectContent>
														{currencies.map((c) => (
														<SelectItem key={c._id} value={c.code}>
															{c.code} ({c.symbol})
														</SelectItem>
													))}
													</SelectContent>
												</Select>
											</div>
											<div>
												<label className="text-sm font-medium">
													Billing Period
												</label>
												<Select
													value={billingPeriod}
													onValueChange={(value: "monthly" | "yearly") => {
														setBillingPeriod(value);
														setIsDirty(true);
													}}
												>
													<SelectTrigger className="mt-1">
														<SelectValue />
													</SelectTrigger>
													<SelectContent>
														<SelectItem value="monthly">Monthly</SelectItem>
														<SelectItem value="yearly">Yearly</SelectItem>
													</SelectContent>
												</Select>
											</div>
										</div>
										<div className="mt-4">
											<label className="text-sm font-medium">
												Custom Terms (optional)
											</label>
											<textarea
												value={customTerms}
												onChange={(e) => {
													setCustomTerms(e.target.value);
													setIsDirty(true);
												}}
												className="mt-1 w-full h-20 px-3 py-2 border rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
												placeholder="Any special terms or conditions..."
											/>
										</div>
									</CardContent>
								)}
							</Card>

							{/* Notes */}
							<Card>
								<CardHeader>
									<CardTitle className="flex items-center gap-2">
										<FileText className="w-5 h-5" />
										Admin Notes
									</CardTitle>
									<CardDescription>
										Internal notes about this custom package (not visible to the
										organization)
									</CardDescription>
								</CardHeader>
								<CardContent>
									<textarea
										value={notes}
										onChange={(e) => {
											setNotes(e.target.value);
											setIsDirty(true);
										}}
										className="w-full h-24 px-3 py-2 border rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
										placeholder="Add any notes about this custom package..."
									/>
								</CardContent>
							</Card>
						</>
					)}

					{/* Action Buttons */}
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center justify-between">
								<div>
									{showResetConfirm ? (
										<div className="flex items-center gap-2">
											<AlertTriangle className="w-5 h-5 text-yellow-500" />
											<span className="text-sm">
												Reset all customizations?
											</span>
											<Button
												variant="destructive"
												size="sm"
												onClick={() => resetMutation.mutate()}
												disabled={resetMutation.isPending}
											>
												{resetMutation.isPending ? "Resetting..." : "Yes, Reset"}
											</Button>
											<Button
												variant="outline"
												size="sm"
												onClick={() => setShowResetConfirm(false)}
											>
												Cancel
											</Button>
										</div>
									) : (
										<Button
											variant="outline"
											onClick={() => setShowResetConfirm(true)}
											disabled={!customPackage?.customPackage}
										>
											<RotateCcw className="w-4 h-4 mr-2" />
											Reset to Base Plan
										</Button>
									)}
								</div>
								<div className="flex items-center gap-3">
									<Button
										variant="outline"
										onClick={() => {
											// Reload from server
											queryClient.invalidateQueries({
												queryKey: ["custom-package", selectedOrgId],
											});
											setIsDirty(false);
										}}
										disabled={!isDirty}
									>
										Discard Changes
									</Button>
									<Button
										onClick={handleSave}
										disabled={updateMutation.isPending || !isDirty}
									>
										{updateMutation.isPending ? (
											"Saving..."
										) : (
											<>
												<CheckCircle2 className="w-4 h-4 mr-2" />
												Save Changes
											</>
										)}
									</Button>
								</div>
							</div>
						</CardContent>
					</Card>
				</>
			)}

			{/* Empty state */}
			{!selectedOrg && (
				<Card className="border-dashed">
					<CardContent className="py-12 text-center">
						<Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
						<h3 className="font-medium text-gray-600">
							No organization selected
						</h3>
						<p className="text-sm text-muted-foreground mt-1">
							Select an organization above to configure its custom package
						</p>
					</CardContent>
				</Card>
			)}

			{/* Dependency Warning Dialog */}
			<DependencyWarningDialog
				open={dialogOpen}
				onOpenChange={setDialogOpen}
				mode={dialogMode}
				featureName={pendingAction ? getFeatureName(pendingAction.featureSlug) : ""}
				autoGrantedDependencies={autoGrantedDeps}
				affectedDependents={affectedDependents}
				onConfirm={handleDialogConfirm}
				onCancel={handleDialogCancel}
				isLoading={updateMutation.isPending}
			/>
		</div>
	);
}
